import React, { createContext, useContext, useState, useEffect } from 'react';
import safeStorage from '../utils/safeStorage';
import { STORAGE_KEYS } from '../utils/storageKeys';

const AuthContext = createContext();

const bytesToHex = (buffer) => Array.from(new Uint8Array(buffer), (b) => b.toString(16).padStart(2, '0')).join('');

const generateSalt = () => {
  if (typeof window === 'undefined' || !window.crypto?.getRandomValues) {
    throw new Error('Secure random generator is unavailable in this environment');
  }
  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  return bytesToHex(array);
};

const hashPassword = async (password, salt) => {
  if (!password) {
    throw new Error('Password is required');
  }
  if (typeof window === 'undefined' || !window.crypto?.subtle) {
    throw new Error('Secure password hashing is unavailable in this environment');
  }
  const encoder = new TextEncoder();
  const data = encoder.encode(`${salt}:${password}`);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return bytesToHex(digest);
};

const sanitizeUserForSession = (userRecord) => {
  if (!userRecord) return null;
  const { passwordHash, salt, ...safeUser } = userRecord;
  return safeUser;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const savedUser = safeStorage.getJSON(STORAGE_KEYS.USER, null);
    if (savedUser) {
      setUser(savedUser);
    }
  }, []);

  const login = async (email, password) => {
    setIsLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 400));

      const users = safeStorage.getJSON(STORAGE_KEYS.USERS, []);
      const existingUser = users.find((u) => u.email === email);

      if (!existingUser || !existingUser.passwordHash || !existingUser.salt) {
        throw new Error('Invalid credentials');
      }

      const computedHash = await hashPassword(password, existingUser.salt);
      if (computedHash !== existingUser.passwordHash) {
        throw new Error('Invalid credentials');
      }

      const updatedUserRecord = {
        ...existingUser,
        lastLogin: new Date().toISOString(),
      };

      const updatedUsers = users.map((u) => (u.email === email ? updatedUserRecord : u));
      safeStorage.setJSON(STORAGE_KEYS.USERS, updatedUsers);

      const sessionUser = sanitizeUserForSession(updatedUserRecord);
      setUser(sessionUser);
      safeStorage.setJSON(STORAGE_KEYS.USER, sessionUser);
      return sessionUser;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (userData) => {
    setIsLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 400));

      const users = safeStorage.getJSON(STORAGE_KEYS.USERS, []);
      const existingUser = users.find((u) => u.email === userData.email);

      if (existingUser) {
        throw new Error('User already exists with this email');
      }

      const salt = generateSalt();
      const passwordHash = await hashPassword(userData.password, salt);
      const createdAt = new Date().toISOString();

      const newUserRecord = {
        id: `user_${Date.now()}`,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        phone: userData.phone || '',
        address: {
          street: userData.address || '',
          city: userData.city || '',
          state: userData.state || '',
          postcode: userData.postcode || '',
          country: userData.country || 'Australia',
        },
        preferences: {
          currency: 'AUD',
          language: 'en',
          newsletter: userData.newsletter ?? true,
          emailUpdates: true,
        },
        orderHistory: [],
        wishlist: [],
        reviews: [],
        createdAt,
        lastLogin: createdAt,
        passwordHash,
        salt,
      };

      safeStorage.setJSON(STORAGE_KEYS.USERS, [...users, newUserRecord]);

      const sessionUser = sanitizeUserForSession(newUserRecord);
      setUser(sessionUser);
      safeStorage.setJSON(STORAGE_KEYS.USER, sessionUser);
      return sessionUser;
    } catch (error) {
      console.error('Signup failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    safeStorage.removeItem(STORAGE_KEYS.USER);
  };

  const updateUser = (updates) => {
    if (!user) return null;

    const updatedSessionUser = { ...user, ...updates };
    setUser(updatedSessionUser);
    safeStorage.setJSON(STORAGE_KEYS.USER, updatedSessionUser);

    const users = safeStorage.getJSON(STORAGE_KEYS.USERS, []);
    const updatedUsers = users.map((record) =>
      record.id === user.id ? { ...record, ...updates } : record
    );
    safeStorage.setJSON(STORAGE_KEYS.USERS, updatedUsers);

    return updatedSessionUser;
  };

  const getCustomerOrders = () => {
    if (!user) return [];
    try {
      const allOrders = safeStorage.getJSON(STORAGE_KEYS.ORDERS, []);
      return allOrders.filter((order) => order.email === user.email);
    } catch (error) {
      console.error('Failed to get customer orders:', error);
      return [];
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        login,
        signup,
        logout,
        updateUser,
        getCustomerOrders,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    console.warn('useAuth used outside AuthProvider - returning default values');
    return {
      user: null,
      isLoading: false,
      login: async () => {
        throw new Error('Auth not available');
      },
      signup: async () => {
        throw new Error('Auth not available');
      },
      logout: () => {},
      updateUser: () => {},
      getCustomerOrders: () => [],
    };
  }
  return context;
};
